import { Hidden } from '@material-ui/core';
import React, { useContext } from 'react';

import { ChannelsSideDrawer } from 'src/components/ChannelsSideDrawer';
import { RouteContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { CreateActionModalProps } from 'src/legacy/components/Channels/BaseChannelSidebar';
import {
  ChannelType,
  useChannelForm,
} from 'src/legacy/components/Channels/useChannelForm';
import { useGetChannelModalTexts } from 'src/legacy/components/Channels/useGetChannelModalTexts';
import { FormModal } from 'src/legacy/components/Modals';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';

import { ChannelsListToolbar } from './ChannelListToolbar';

const CREATE_CHANNEL_FALLBACK_TITLE = 'Create a new channel';

export interface ChannelsSidebarProps {
  entity: ChannelType;
  onCreate?: (values: any) => Promise<void>;
  initialCreateFormValue: any;
  modalTitle?: string;
  hasChannels?: boolean;
  pageTitle: React.ReactNode;
  CreateModal?: React.FC<CreateActionModalProps>;
  headerActionsSlot?: React.ReactNode;
  allowKeyboardShortcuts: boolean;
  isTransparentSearchStyle?: boolean;
  searchPlaceholder?: string;
}

export const ChannelsSidebar: React.FC<ChannelsSidebarProps> = React.memo(
  ({
    onCreate,
    entity,
    children,
    initialCreateFormValue,
    modalTitle,
    hasChannels = true,
    pageTitle,
    CreateModal,
    headerActionsSlot,
    allowKeyboardShortcuts = true,
    isTransparentSearchStyle = false,
    searchPlaceholder = '',
  }) => {
    const { query } = useContext(RouteContext);
    const { channelId: activeChannelId } = query;
    const bannerOptions = useAppSelector((state) => state.ui.bannerOptions);
    const { data: { moduleSettings } = {} } = useGetInstallsQuery();
    const [openCreateModal, setOpenCreateModal] = React.useState(false);

    const handleOpenModal = () => setOpenCreateModal(true);
    const handleCloseModal = () => setOpenCreateModal(false);
    const { positiveAction } = useGetChannelModalTexts(entity, moduleSettings);

    const createButtonProps = React.useMemo(() => {
      if (CreateModal) {
        return {
          onClick: handleOpenModal,
        };
      }
      return undefined;
    }, [CreateModal]);

    return (
      <>
        {hasChannels && (
          <Hidden xsDown={Boolean(activeChannelId)}>
            <ChannelsSideDrawer hasBanner={Boolean(bannerOptions)}>
              <ChannelsListToolbar
                createButtonProps={createButtonProps}
                pageTitle={pageTitle}
                actionsSlot={headerActionsSlot}
                allowKeyboardShortcuts={allowKeyboardShortcuts}
                isTransparentSearchStyle={isTransparentSearchStyle}
                searchPlaceholder={searchPlaceholder}
              />
              {children}
            </ChannelsSideDrawer>
          </Hidden>
        )}
        {CreateModal ? (
          <CreateModal onClose={handleCloseModal} open={openCreateModal} />
        ) : (
          <>
            {onCreate && (
              <FormModal
                buttonLabel={positiveAction}
                initialFormValue={initialCreateFormValue}
                entityName="channel"
                handleSave={onCreate}
                useFormHook={() => useChannelForm(entity)}
                open={openCreateModal}
                onClose={handleCloseModal}
                createButtonProps={{ fullWidth: true }}
                title={modalTitle || CREATE_CHANNEL_FALLBACK_TITLE}
              />
            )}
          </>
        )}
      </>
    );
  },
);
